const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Fonction pour générer une date locale en format ISO
const getLocalISOString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  const milliseconds = String(now.getMilliseconds()).padStart(3, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}Z`;
};

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
  }

  // Méthode générique pour les requêtes HTTP
  async request(endpoint, options = {}) {
    let url = `${this.baseURL}${endpoint}`;
    const method = (options.method || 'GET').toUpperCase();
    if (method === 'GET') {
      const sep = url.includes('?') ? '&' : '?';
      url = `${url}${sep}_=${Date.now()}`;
    }
    
    // Ajouter le token d'authentification si disponible
    // Essayer d'abord le token admin, puis le token patient
    const adminToken = localStorage.getItem('adminToken');
    const patientToken = localStorage.getItem('token');
    const token = adminToken || patientToken;
    
    // Configuration par défaut
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      cache: method === 'GET' ? 'no-store' : (options.cache || 'default'),
      ...options
    };

    // Ajouter le token après avoir défini les headers
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      if (process.env.NODE_ENV === 'development') {
    console.log('URL:', url);
  }
      if (process.env.NODE_ENV === 'development') {
    console.log('Config:', config);
  }
      
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        // Si c'est une erreur 401 (token expiré), essayer de le renouveler
        if (response.status === 401 && token) {
          const refreshed = await this.refreshToken();
          if (refreshed) {
            // Réessayer avec le nouveau token
            const newToken = localStorage.getItem('adminToken') || localStorage.getItem('token');
            config.headers['Authorization'] = `Bearer ${newToken}`;
            const retryResponse = await fetch(url, config);
            const retryData = await retryResponse.json();
            
            if (!retryResponse.ok) {
              throw new Error(retryData.message || 'Erreur de requête');
            }
            return retryData;
          }
        }
        
        // Si c'est une erreur de rate limiting, attendre un peu et réessayer
        if (response.status === 429 || data.message?.includes('Trop de requêtes')) {
          if (process.env.NODE_ENV === 'development') {
    console.log('Rate limiting détecté, attente de 2 secondes...');
  }
          await new Promise(resolve => setTimeout(resolve, 2000));
          // Réessayer une fois
          const retryResponse = await fetch(url, config);
          const retryData = await retryResponse.json();
          
          if (!retryResponse.ok) {
            throw new Error(retryData.message || 'Erreur de requête');
          }
          return retryData;
        }
        throw new Error(data.message || 'Erreur de requête');
      }

      return data;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
    console.error('Erreur API:', error);
  }
      throw error;
    }
  }

  // Renouveler le token automatiquement
  async refreshToken() {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        return false;
      }

      const response = await fetch(`${this.baseURL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refreshToken })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Mettre à jour le token dans le localStorage
        if (data.user.role === 'admin') {
          localStorage.setItem('adminToken', data.accessToken);
        } else {
          localStorage.setItem('token', data.accessToken);
        }
        return true;
      } else {
        // Refresh token invalide, déconnexion
        this.logout();
        return false;
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
    console.error('Erreur renouvellement token:', error);
  }
      this.logout();
      return false;
    }
  }

  // Déconnexion
  logout() {
    const refreshToken = localStorage.getItem('refreshToken');
    if (refreshToken) {
      // Révoquer le refresh token côté serveur
      fetch(`${this.baseURL}/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refreshToken })
      }).catch(error => {
        if (process.env.NODE_ENV === 'development') {
    console.error('Erreur lors de la déconnexion:', error);
  }
      });
    }

    // Nettoyer le localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('adminToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('adminUser');

    // Rediriger vers la page de connexion
    window.location.href = '/login';
  }

  // Authentification patient
  async patientLogin(patientId, password) {
    return this.request('/auth/patient/login', {
      method: 'POST',
      body: JSON.stringify({ patientId, password })
    });
  }

  // Authentification admin
  async adminLogin(username, password) {
    return this.request('/auth/admin/login', {
      method: 'POST',
      body: JSON.stringify({ username, password })
    });
  }

  // Vérifier le token
  async verifyToken() {
    return this.request('/auth/verify');
  }

  // Obtenir la timeline d'un patient
  async getPatientTimeline(patientId) {
    return this.request(`/timeline/patient/${patientId}`);
  }

  // Marquer une tâche comme complétée
  async markTaskCompleted(patientId, taskId) {
    return this.request('/timeline/task', {
      method: 'POST',
      body: JSON.stringify({ patientId, taskId })
    });
  }

  // Marquer une tâche comme non complétée
  async markTaskIncomplete(patientId, taskId) {
    return this.request('/timeline/task', {
      method: 'DELETE',
      body: JSON.stringify({ patientId, taskId })
    });
  }

  // Obtenir tous les patients (admin)
  async getAllPatients() {
    return this.request('/patients');
  }

  // Créer un nouveau patient (admin)
  async createPatient(patientData) {
    return this.request('/patients', {
      method: 'POST',
      body: JSON.stringify(patientData)
    });
  }

  // Modifier un patient (admin)
  async updatePatient(patientId, patientData) {
    return this.request(`/patients/${patientId}`, {
      method: 'PUT',
      body: JSON.stringify(patientData)
    });
  }

  // Supprimer un patient (admin)
  async deletePatient(patientId) {
    return this.request(`/patients/${patientId}`, {
      method: 'DELETE'
    });
  }

  // Obtenir les statistiques (admin)
  async getStats() {
    return this.request('/timeline/stats');
  }

  // Obtenir les statistiques détaillées de la timeline (admin)
  async getTimelineStats() {
    return this.request('/timeline/stats');
  }

  // Obtenir l'historique détaillé d'un patient (admin)
  async getPatientHistory(patientId) {
    const cacheKey = `patient_history_${patientId}`;
    const cached = this.cache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      if (process.env.NODE_ENV === 'development') {
    console.log('Utilisation du cache pour getPatientHistory:', patientId);
  }
      return cached.data;
    }
    
    const data = await this.request(`/timeline/patient/${patientId}/history`);
    
    // Mettre en cache
    this.cache.set(cacheKey, {
      data,
      timestamp: Date.now()
    });
    
    return data;
  }

  // Soumettre un questionnaire
  async submitQuestionnaire(responseData) {
    return this.request('/questionnaire/submit', {
      method: 'POST',
      body: JSON.stringify(responseData)
    });
  }

  // Obtenir toutes les réponses OHS (admin)
  async getAllOHSResponses() {
    if (process.env.NODE_ENV === 'development') {
    console.log('Appel getAllOHSResponses...');
  }
    console.log('Token admin:', localStorage.getItem('adminToken'));
    console.log('Token patient:', localStorage.getItem('token'));
    const result = await this.request('/questionnaire/ohs/all');
    if (process.env.NODE_ENV === 'development') {
    console.log('Résultat getAllOHSResponses:', result);
  }
    return result;
  }

  
  // Obtenir toutes les réponses SF-36 (admin)
  async getAllSF36Responses() {
    if (process.env.NODE_ENV === 'development') {
    console.log('Appel getAllSF36Responses...');
  }
    const result = await this.request('/questionnaire/sf36/all');
    if (process.env.NODE_ENV === 'development') {
    console.log('Résultat getAllSF36Responses:', result);
  }
    return result;
  }

  // Obtenir toutes les réponses QoR-15 (admin)
  async getAllQoR15Responses() {
    if (process.env.NODE_ENV === 'development') {
    console.log('Appel getAllQoR15Responses...');
  }
    const result = await this.request('/questionnaire/qor15/all');
    if (process.env.NODE_ENV === 'development') {
    console.log('Résultat getAllQoR15Responses:', result);
  }
    return result;
  }


  // Obtenir toutes les réponses de questionnaires (admin)
  async getAllQuestionnaireResponses() {
    if (process.env.NODE_ENV === 'development') {
    console.log('Appel getAllQuestionnaireResponses...');
  }
    console.log('Token admin:', localStorage.getItem('adminToken'));
    console.log('Token patient:', localStorage.getItem('token'));
    const result = await this.request('/questionnaire/all');
    if (process.env.NODE_ENV === 'development') {
    console.log('Résultat getAllQuestionnaireResponses:', result);
  }
    return result;
  }

  // Obtenir la réponse d'un questionnaire spécifique
  async getQuestionnaireResponse(patientId, taskId) {
    return this.request(`/questionnaire/responses/${patientId}/${taskId}`);
  }

  // Mettre à jour une réponse de questionnaire existante
  async updateQuestionnaireResponse(patientId, taskId, questionnaireType, responses, score, language = 'de') {
    return this.request(`/questionnaire/update/${patientId}/${taskId}`, {
      method: 'PUT',
      body: JSON.stringify({
        questionnaireType,
        responses,
        score,
        language,
        submissionDate: getLocalISOString()
      })
    });
  }

  // Obtenir l'historique complet d'un questionnaire
  async getQuestionnaireHistory(patientId, taskId) {
    return this.request(`/questionnaire/history/${patientId}/${taskId}`);
  }

  // Obtenir la dernière version d'un questionnaire
  async getLatestQuestionnaireVersion(patientId, taskId) {
    return this.request(`/questionnaire/latest/${patientId}/${taskId}`);
  }

  // Obtenir l'historique des mesures d'un patient
  async getPatientMeasurements(patientId) {
    return this.request(`/questionnaire/measurements/patient/${patientId}`);
  }

  // Vérifier la santé du serveur
  async healthCheck() {
    return this.request('/health');
  }

  // Méthode GET générique
  async get(endpoint) {
    return this.request(endpoint, { method: 'GET' });
  }
}

// Instance singleton
const apiService = new ApiService();
export default apiService; 